import { useMemo, useState } from "react";
import { momentCaptions } from "@/mocks/quotes";
import { useCopy } from "@/hooks/useCopy";

const tones = ["全部", "伤感", "深夜", "温柔", "自愈", "释然", "祝愿", "克制"];

const tips = [
  { icon: "ri-image-line", title: "配图建议", text: "文字越短，越适合留白多的照片，比如空镜、背影、窗景。" },
  { icon: "ri-font-size", title: "长度选择", text: "朋友圈最多展示两行，优先挑 20 字以内的短句。" },
  { icon: "ri-emotion-line", title: "语气把握", text: "伤感可以有，但别过度渲染，克制的表达反而更耐看。" },
];

export default function MomentCaptions() {
  const [tone, setTone] = useState("全部");
  const { copiedId, copy } = useCopy();

  const list = useMemo(
    () => (tone === "全部" ? momentCaptions : momentCaptions.filter((c) => c.tone === tone)),
    [tone],
  );

  return (
    <section id="captions" className="w-full bg-background-50 py-16 md:py-24 scroll-mt-16">
      <div className="w-full max-w-6xl mx-auto px-4 md:px-8">
        <div className="max-w-2xl">
          <span className="text-xs tracking-widest text-primary-600 uppercase">Captions</span>
          <h2 className="mt-3 font-heading text-2xl md:text-3xl font-semibold text-foreground-950">
            朋友圈配文：文艺短句随手可用
          </h2>
          <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-700">
            按情绪挑一条，点复制就能直接发。这些短句偏向克制与留白，
            搭配一张干净的图，比长篇感慨更容易被读懂。
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
          <div className="lg:col-span-2">
            <div className="flex flex-wrap gap-2" role="tablist" aria-label="配文情绪筛选">
              {tones.map((t) => {
                const isActive = tone === t;
                return (
                  <button
                    key={t}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => setTone(t)}
                    className={`px-3.5 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                      isActive
                        ? "bg-foreground-900 text-background-50"
                        : "bg-background-100 text-foreground-700 hover:bg-background-200"
                    }`}
                  >
                    {t}
                  </button>
                );
              })}
            </div>

            <ul className="mt-6 flex flex-col divide-y divide-background-200/70 border-t border-b border-background-200/70">
              {list.map((c) => {
                const isCopied = copiedId === c.id;
                return (
                  <li key={c.id} className="flex items-center gap-4 py-4">
                    <span className="shrink-0 px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-900 text-xs">
                      {c.tone}
                    </span>
                    <p className="flex-1 font-heading text-sm md:text-base text-foreground-900 leading-relaxed">
                      {c.text}
                    </p>
                    <button
                      type="button"
                      onClick={() => copy(c.id, c.text)}
                      className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs transition-colors cursor-pointer whitespace-nowrap ${
                        isCopied
                          ? "bg-accent-500 text-background-50"
                          : "border border-background-300 text-foreground-700 hover:bg-accent-100 hover:text-accent-900"
                      }`}
                      aria-label={`复制配文：${c.text}`}
                    >
                      <i className={isCopied ? "ri-check-line" : "ri-file-copy-line"}></i>
                      {isCopied ? "已复制" : "复制"}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          <aside className="flex flex-col gap-4">
            <div className="rounded-lg bg-accent-50 border border-accent-100 p-6">
              <h3 className="font-heading text-base font-semibold text-foreground-950 flex items-center gap-2">
                <i className="ri-lightbulb-line text-accent-700"></i>
                配文使用小贴士
              </h3>
              <div className="mt-5 flex flex-col gap-4">
                {tips.map((tip) => (
                  <div key={tip.title} className="flex items-start gap-3">
                    <span className="shrink-0 w-8 h-8 flex items-center justify-center rounded-md bg-background-50 text-accent-700">
                      <i className={tip.icon}></i>
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{tip.title}</p>
                      <p className="mt-1 text-xs leading-relaxed text-foreground-600">{tip.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-lg bg-secondary-50 border border-secondary-100 p-6">
              <p className="font-heading text-sm leading-loose text-foreground-900">
                「如果快乐太难，那就先祝我们平安。」
              </p>
              <a
                href="#wall"
                className="mt-4 inline-flex items-center gap-1.5 text-xs text-secondary-700 hover:text-secondary-900 cursor-pointer"
              >
                去共鸣墙看看别人的心情
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}